import React, { useState, useEffect } from "react"
import { apiClient } from "~/api/client"
import AuthenticatedLayout from "~/components/AuthenticatedLayout"
import type { User } from "~/types"

const Admin: React.FC = () => {
  const [users, setUsers] = useState<User[]>([])
  const [usersLoading, setUsersLoading] = useState(true)
  const [userEmail, setUserEmail] = useState("")
  const [userName, setUserName] = useState("")
  const [userPassword, setUserPassword] = useState("")
  const [userSuccess, setUserSuccess] = useState("")
  const [userError, setUserError] = useState("")
  const [userLoading, setUserLoading] = useState(false)

  const [updateUserId, setUpdateUserId] = useState("")
  const [updatePassword, setUpdatePassword] = useState("")
  const [updateSuccess, setUpdateSuccess] = useState("")
  const [updateError, setUpdateError] = useState("")
  const [updateLoading, setUpdateLoading] = useState(false)

  const [entryId, setEntryId] = useState("")
  const [entrySuccess, setEntrySuccess] = useState("")
  const [entryError, setEntryError] = useState("")
  const [entryLoading, setEntryLoading] = useState(false)

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    setUsersLoading(true)
    try {
      const data = await apiClient.getUsers()
      setUsers(data)
    } catch {
      // Failed to load users
    } finally {
      setUsersLoading(false)
    }
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setUserSuccess("")
    setUserError("")
    setUserLoading(true)

    try {
      await apiClient.createUser({
        email: userEmail,
        name: userName,
        password: userPassword,
      })
      setUserSuccess(`User ${userEmail} created successfully`)
      setUserEmail("")
      setUserName("")
      setUserPassword("")
      loadUsers() // Refresh user list
    } catch (err) {
      setUserError(err instanceof Error ? err.message : "Failed to create user")
    } finally {
      setUserLoading(false)
    }
  }

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setUpdateSuccess("")
    setUpdateError("")
    setUpdateLoading(true)

    try {
      await apiClient.updateUser(updateUserId, {
        password: updatePassword,
      })
      setUpdateSuccess(
        `Password for user #${updateUserId} updated successfully`,
      )
      setUpdateUserId("")
      setUpdatePassword("")
    } catch (err) {
      setUpdateError(
        err instanceof Error ? err.message : "Failed to update user",
      )
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleDeleteEntry = async (e: React.FormEvent) => {
    e.preventDefault()
    setEntrySuccess("")
    setEntryError("")

    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Are you sure you want to delete knowledge entry #${entryId}? This cannot be undone.`,
      )
    ) {
      return
    }

    setEntryLoading(true)

    try {
      await apiClient.deleteEntry(entryId)
      setEntrySuccess(`Entry #${entryId} deleted successfully`)
      setEntryId("")
    } catch (err) {
      setEntryError(
        err instanceof Error ? err.message : "Failed to delete entry",
      )
    } finally {
      setEntryLoading(false)
    }
  }

  return (
    <AuthenticatedLayout>
      <div style={{ maxWidth: "800px", margin: "0 auto", padding: "0 24px" }}>
        {/* User List Section */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            border: "1px solid #ccc",
            padding: "16px",
            marginBottom: "24px",
          }}
        >
          <h2 style={{ marginTop: 0, marginBottom: "16px" }}>All Users</h2>

          {usersLoading ? (
            <div style={{ textAlign: "center", padding: "24px" }}>
              Loading...
            </div>
          ) : users.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "24px", color: "#666" }}
            >
              No users found
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderBottom: "1px solid #ccc",
                  }}
                >
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    ID
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Email
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Name
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    style={{
                      borderBottom: "1px solid #eee",
                      cursor: "pointer",
                    }}
                    onClick={() => setUpdateUserId(user.id.toString())}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f8f9fa")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "white")
                    }
                  >
                    <td style={{ padding: "12px 16px" }}>{user.id}</td>
                    <td style={{ padding: "12px 16px" }}>{user.email}</td>
                    <td style={{ padding: "12px 16px" }}>{user.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create User Section */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            border: "1px solid #ccc",
            padding: "16px",
            marginBottom: "24px",
          }}
        >
          <h2 style={{ marginTop: 0, marginBottom: "16px" }}>Create User</h2>

          <form onSubmit={handleCreateUser}>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: 600,
                }}
              >
                Email
              </label>
              <input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: 600,
                }}
              >
                Name
              </label>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: 600,
                }}
              >
                Password
              </label>
              <input
                type="password"
                value={userPassword}
                onChange={(e) => setUserPassword(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />
            </div>

            {userSuccess && (
              <div
                style={{
                  marginBottom: "16px",
                  padding: "12px",
                  backgroundColor: "#d4edda",
                  color: "#155724",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                {userSuccess}
              </div>
            )}

            {userError && (
              <div
                style={{
                  marginBottom: "16px",
                  padding: "12px",
                  backgroundColor: "#fee",
                  color: "#c33",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                {userError}
              </div>
            )}

            <button
              type="submit"
              disabled={userLoading}
              style={{
                padding: "12px 24px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: userLoading ? "not-allowed" : "pointer",
                opacity: userLoading ? 0.6 : 1,
              }}
            >
              {userLoading ? "Creating..." : "Create User"}
            </button>
          </form>
        </div>

        {/* Reset Password Section */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            border: "1px solid #ccc",
            padding: "16px",
            marginBottom: "24px",
          }}
        >
          <h2 style={{ marginTop: 0, marginBottom: "16px" }}>
            Reset User Password
          </h2>

          <form onSubmit={handleUpdateUser}>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: 600,
                }}
              >
                User ID
              </label>
              <input
                type="text"
                value={updateUserId}
                onChange={(e) => setUpdateUserId(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: 600,
                }}
              >
                New Password
              </label>
              <input
                type="password"
                value={updatePassword}
                onChange={(e) => setUpdatePassword(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />
            </div>

            {updateSuccess && (
              <div
                style={{
                  marginBottom: "16px",
                  padding: "12px",
                  backgroundColor: "#d4edda",
                  color: "#155724",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                {updateSuccess}
              </div>
            )}

            {updateError && (
              <div
                style={{
                  marginBottom: "16px",
                  padding: "12px",
                  backgroundColor: "#fee",
                  color: "#c33",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                {updateError}
              </div>
            )}

            <button
              type="submit"
              disabled={updateLoading}
              style={{
                padding: "12px 24px",
                backgroundColor: "#ffc107",
                color: "#000",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: updateLoading ? "not-allowed" : "pointer",
                opacity: updateLoading ? 0.6 : 1,
              }}
            >
              {updateLoading ? "Updating..." : "Reset Password"}
            </button>
          </form>
        </div>

        {/* Delete Entry Section */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            border: "1px solid #ccc",
            padding: "16px",
          }}
        >
          <h2 style={{ marginTop: 0, marginBottom: "16px" }}>
            Delete Knowledge Entry
          </h2>

          <form onSubmit={handleDeleteEntry}>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: 600,
                }}
              >
                Entry ID
              </label>
              <input
                type="text"
                value={entryId}
                onChange={(e) => setEntryId(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />
            </div>

            {entrySuccess && (
              <div
                style={{
                  marginBottom: "16px",
                  padding: "12px",
                  backgroundColor: "#d4edda",
                  color: "#155724",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                {entrySuccess}
              </div>
            )}

            {entryError && (
              <div
                style={{
                  marginBottom: "16px",
                  padding: "12px",
                  backgroundColor: "#fee",
                  color: "#c33",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                {entryError}
              </div>
            )}

            <button
              type="submit"
              disabled={entryLoading}
              style={{
                padding: "12px 24px",
                backgroundColor: "#dc3545",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: entryLoading ? "not-allowed" : "pointer",
                opacity: entryLoading ? 0.6 : 1,
              }}
            >
              {entryLoading ? "Deleting..." : "Delete Entry"}
            </button>
          </form>
        </div>
      </div>
    </AuthenticatedLayout>
  )
}

export default Admin
